import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { TeleportTarget } from '../../../types/Messages'

export interface TeleportPingRequest {
    requesterSessionId: string
    requesterName: string
}

export interface RecentTeleportEntry {
    target: TeleportTarget
    /** The traveler's stable Back4App user id (player-target entries only) — lets a recent still
     *  resolve to the right person after they reconnect under a new sessionId. Sibling to target,
     *  not part of it: target stays the wire-shape TeleportTarget sent in TELEPORT_REQUEST. */
    userId?: string
    at: number
}

interface TeleportState {
    pickerOpen: boolean
    /** DND notify-and-wait ping awaiting this client's Allow/Not now response (SC-394). */
    pingRequest: TeleportPingRequest | null
    /** Local-device-only "last teleported to" list (SC-396) — never synced, unlike Phase 6's favourites. */
    recents: RecentTeleportEntry[]
}

const RECENTS_STORAGE_KEY = 'sevv:teleport:recents:v1'
const RECENTS_CAP = 8

function targetKey(target: TeleportTarget): string {
    return target.type === 'room' ? `room:${target.roomId}` : `player:${target.sessionId}`
}

function loadRecents(): RecentTeleportEntry[] {
    try {
        const raw = localStorage.getItem(RECENTS_STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.slice(0, RECENTS_CAP)
    } catch {
        return []
    }
}

function saveRecents(recents: RecentTeleportEntry[]): void {
    try {
        localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recents))
    } catch {
        // Best-effort only — a private window or full quota shouldn't break teleporting.
    }
}

const initialState: TeleportState = {
    pickerOpen: false,
    pingRequest: null,
    recents: loadRecents(),
}

const teleportSlice = createSlice({
    name: 'teleport',
    initialState,
    reducers: {
        openTeleportPicker: (state) => {
            state.pickerOpen = true
        },
        closeTeleportPicker: (state) => {
            state.pickerOpen = false
        },
        toggleTeleportPicker: (state) => {
            state.pickerOpen = !state.pickerOpen
        },
        setTeleportPingRequest: (state, action: PayloadAction<TeleportPingRequest>) => {
            state.pingRequest = action.payload
        },
        // Server withdrew a ping (SC-425). Only clears when it is still the one on screen — the
        // slot is single, so a newer requester's ping must survive an older one's cancel.
        cancelTeleportPingRequest: (state, action: PayloadAction<string>) => {
            if (state.pingRequest?.requesterSessionId === action.payload) state.pingRequest = null
        },
        clearTeleportPingRequest: (state) => {
            state.pingRequest = null
        },
        recordRecentTeleport: (state, action: PayloadAction<{ target: TeleportTarget; userId?: string }>) => {
            const { target, userId } = action.payload
            const key = targetKey(target)
            // Dedup by userId first when available — otherwise a reconnect (new sessionId, same
            // person) would add a second recents entry instead of replacing the stale one.
            const deduped = state.recents.filter((entry) =>
                userId ? entry.userId !== userId : targetKey(entry.target) !== key
            )
            state.recents = [{ target, userId, at: Date.now() }, ...deduped].slice(0, RECENTS_CAP)
            saveRecents(state.recents)
        },
    },
})

export const {
    openTeleportPicker,
    closeTeleportPicker,
    toggleTeleportPicker,
    setTeleportPingRequest,
    clearTeleportPingRequest,
    cancelTeleportPingRequest,
    recordRecentTeleport,
} = teleportSlice.actions
export default teleportSlice.reducer

export const selectTeleportPingRequest = (state: { teleport: TeleportState }): TeleportPingRequest | null =>
    state.teleport.pingRequest

const selectRecents = (state: { teleport: TeleportState }): RecentTeleportEntry[] => state.teleport.recents

// Memoized (not a fresh .filter() per call) — an unmemoized derived-array selector otherwise
// triggers "selector returned a different result" warnings/rerenders on every dispatch, even one
// unrelated to teleport recents, since useSelector's default equality check is reference-based.
export const selectRecentRooms = createSelector([selectRecents], (recents) =>
    recents.filter((entry) => entry.target.type === 'room')
)

export const selectRecentPlayers = createSelector([selectRecents], (recents) =>
    recents.filter((entry) => entry.target.type === 'player')
)
